<template>
  <span>
    <FormattedMessage
      v-if="documentLinks.length"
      class="caption whitespace-pre-line"
      :definition="termsLabel"
    >
      <template #documents>
        <template v-for="(link, index) in documentLinks" :key="link.url">
          <RevLink
            target="_blank"
            :to="link.url"
            :tracking="{
              name: link.trackingName,
              zone: 'cart_insurance_selected',
            }"
          >
            {{ i18n(link.label) }}
          </RevLink>

          <template v-if="documentLinks.length - index > 2">, </template>
          <template v-else-if="documentLinks.length - index > 1">{{
            ` ${i18n(translations.and)} `
          }}</template>
        </template>
      </template>
    </FormattedMessage>

    <FormattedMessage
      v-if="shouldDisplayFaqLink"
      class="caption whitespace-pre-line"
      :definition="translations.faqLabel"
    >
      <template #faqlink>
        <RevLink
          class="caption"
          target="_blank"
          :to="i18n(translations.faqLinkValue)"
          :tracking="{
            name: 'terms_document',
            zone: 'cart_insurance_selected',
          }"
        >
          {{ i18n(translations.faqLinkLabel) }}
        </RevLink>
      </template>
    </FormattedMessage>
  </span>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type { InsuranceOffer } from '@backmarket/http-api/src/api-models/InsuranceOffer'
import type { InsuranceOfferCompliancyDocument } from '@backmarket/http-api/src/api-models/InsuranceOfferCompliancyDocument'
import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { RevLink } from '@ds/components/Link'

import { DOCUMENT_KINDS } from '../../constant'

import translations from './Terms.translations'

const props = withDefaults(
  defineProps<{
    documents: InsuranceOfferCompliancyDocument[]
    offerType: InsuranceOffer['type']
    paymentFrequency: InsuranceOffer['paymentFrequency']
  }>(),
  { documents: () => [] },
)

const i18n = useI18n()
const { market } = useMarketplace()

const termsUrl = computed(
  () =>
    props.documents.find(
      (doc) => doc.kind === DOCUMENT_KINDS.TERMS_AND_CONDITIONS,
    )?.url,
)
const ipidUrl = computed(
  () => props.documents.find((doc) => doc.kind === DOCUMENT_KINDS.IPID)?.url,
)
const recapUrl = computed(
  () =>
    props.documents.find((doc) => doc.kind === DOCUMENT_KINDS.BACKMARKET_RECAP)
      ?.url,
)
const documentLinks = computed(() =>
  [
    {
      url: termsUrl.value,
      label: translations.terms,
      trackingName: 'terms_document',
    },
    {
      url: ipidUrl.value,
      label: translations.ipid,
      trackingName: 'ipid_document',
    },
    {
      url: recapUrl.value,
      label: translations.recap,
      trackingName: 'recap_document',
    },
  ].filter((link) => Boolean(link.url)),
)

const termsLabel = computed(() => {
  const TERMS_LABELS = {
    INSURANCE: {
      UPFRONT: translations.insuranceUpfront,
      MONTHLY: translations.insuranceMonthly,
      ANNUALLY: translations.insuranceAnnually,
      BIANNUALLY: translations.insuranceBiennial,
    },
    PROTECTION_SERVICE: {
      UPFRONT: translations.protectionServiceUpfront,
      MONTHLY: translations.protectionServiceMonthly,
      ANNUALLY: translations.protectionServiceAnnually,
      BIANNUALLY: translations.protectionServiceBiennial,
    },
  }

  return TERMS_LABELS[props.offerType][props.paymentFrequency]
})

const shouldDisplayFaqLink = computed(() => {
  const RECURRING_PAYMENT_FREQUENCIES = ['MONTHLY', 'ANNUALLY', 'BIANNUALLY']

  return (
    market.countryCode === 'US' &&
    RECURRING_PAYMENT_FREQUENCIES.includes(props.paymentFrequency)
  )
})
</script>
